'use client'

import Image from 'next/image'
import { AspectRatio, Box, Button, Stack, Text } from '@chakra-ui/react'
import { Link, useRouter } from '@/i18n/navigation'
import { IRecipe } from '@/_types'
import { QuestionIcon } from '@/_assets/icon-components'
import { useCustomLocale } from '@/_hooks/useCustomLocale'
import { useTranslations } from 'next-intl'
import { getBrandSlug } from '@/_lib/brand-href'

const stripHtml = (html?: string) =>
  (html || '').replace(/<[^>]*>/g, '').trim()

const Desktop = ({
  data,
  shipperId,
}: {
  data: IRecipe
  shipperId: string
}) => {
  const t = useTranslations()
  const currentLocale = useCustomLocale()
  const router = useRouter()
  const description = stripHtml(data?.description?.[currentLocale])
  const href = `/recipes/${data?.slug}?brand=${getBrandSlug(shipperId)}`

  return (
    <Stack
      gap={0}
      w='full'
      h='full'
      rounded={16}
      cursor='pointer'
      bg='background.card'
      border='1px solid #F4F4F4'
      boxShadow='0px 4px 20px 0px #0000000F'
      justifyContent='space-between'
      overflow='hidden'
    >
      <Link href={href}>
        <Box px={4} pt={4}>
          <Box bgColor='background' rounded='xl' overflow='hidden'>
            <AspectRatio ratio={3 / 2} position='relative' maxH='290px'>
              {data?.images?.[0] ? (
                <Image
                  src={process.env.BASE_URL + data.images[0]}
                  alt={data?.title?.[currentLocale]}
                  style={{ objectFit: 'cover' }}
                  fill
                  sizes='(max-width: 1200px) 50vw, 33vw'
                />
              ) : (
                <Box
                  display='flex'
                  bg='background'
                  alignItems='center'
                  justifyContent='center'
                >
                  <QuestionIcon />
                </Box>
              )}
            </AspectRatio>
          </Box>
        </Box>
        <Stack gap={1} px={4} pt={4} pb={3} minH='78px'>
          <Text
            fontSize='md'
            fontWeight={500}
            lineHeight={1.2}
            lineClamp={2}
            color='text'
          >
            {data?.title?.[currentLocale]}
          </Text>
          <Text
            fontSize='sm'
            lineClamp={2}
            fontWeight={450}
            lineHeight={1.3}
            color='text.muted'
          >
            {description}
          </Text>
        </Stack>
      </Link>
      <Box px={4} pb={4}>
        <Button
          w='full'
          rounded={12}
          variant='subtle'
          fontWeight={600}
          height={12}
          size='lg'
          bg='product_button'
          color='product_button.text'
          _hover={{ bgColor: 'product_button.hover' }}
          onClick={() => router.push(href)}
        >
          {t('read_more')}
        </Button>
      </Box>
    </Stack>
  )
}

export default Desktop
